import React, { useMemo } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../shared/animationVariants';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { getGuardTelemetry } from '../../../practice';
import { Activity, ShieldAlert, BarChart2, AlertTriangle, Target } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
export function SessionAnalyticsPage() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const data = useMemo(() => {
    const allAccounts = accounts?.accountsById ? Object.values(accounts.accountsById) : [];
    const learners = allAccounts.filter(a => a.role === 'learner');
    let totalAttempts = 0;
    let correctAttempts = 0;
    let frustrationEvents = 0;
    const tierCounts: Record<number, number> = {};
    learners.forEach(learner => {
      const lData = getLearnerData(learner.id);
      if (!lData?.familyMastery) return;
      Object.values(lData.familyMastery).forEach(mastery => {
        if (!mastery.history) return;
        let consecutiveErrors = 0;
        mastery.history.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            correctAttempts++;
            consecutiveErrors = 0;
          } else {
            consecutiveErrors++;
            if (consecutiveErrors === 3) {
              frustrationEvents++;
            }
          }
          if (attempt.tier) {
            tierCounts[attempt.tier] = (tierCounts[attempt.tier] || 0) + 1;
          }
        });
      });
    });
    const guardData = getGuardTelemetry();
    return {
      totalAttempts,
      accuracy: totalAttempts > 0 ? Math.round(correctAttempts / totalAttempts * 100) : 0,
      frustrationEvents,
      tierCounts,
      guardData
    };
  }, [accounts, getLearnerData]);
  const maxTierCount = Math.max(...Object.values(data.tierCounts), 1);
  if (data.totalAttempts === 0 && data.guardData.totalSessions === 0) {
    return <PageLayout title="Sessie Analytics" subtitle="Inzicht in het gedrag van de Adaptive Engine (P-15)" data-id="element-7120" data-mp="1t2ro2s6q968r">
        <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-7121" data-mp="26uo9kddw4hw6">
          <BarChart2 className="w-12 h-12 text-slate-300 mb-4" data-id="element-7122" data-mp="221v7xydpxpob" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2" data-id="element-7123" data-mp="2gr1cm1vbs83l">
            Nog geen sessiedata
          </h3>
          <p className="text-sm text-slate-500 max-w-md" data-id="element-7124" data-mp="2breouyhvf9gj">
            Zodra leerlingen oefensessies voltooien, verschijnen hier
            statistieken over accuratesse, tier distributie en guard telemetrie.
          </p>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Sessie Analytics" subtitle="Inzicht in het gedrag van de Adaptive Engine (P-15)" data-id="element-7125" data-mp="vdy97aw5ekah">
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-7126" data-mp="qfwoklj2qcww">
        <motion.div variants={itemVariants} data-id="element-7127" data-mp="371tdb4ohtweu">
          <StatCard label="Gemiddelde Accuratesse" value={`${data.accuracy}%`} icon={<Target className="w-4 h-4 text-emerald-500" data-id="element-7129" data-mp="2t82bkih07gto" />} data-id="element-7128" data-mp="370rzisg3evtf" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7130" data-mp="2x9nmvzdw3n1r">
          <StatCard label="Frustratie Events (P-12)" value={data.frustrationEvents} icon={<AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-7132" data-mp="2oc3iwion7cng" />} data-id="element-7131" data-mp="2jbfum4rst9wx" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7133" data-mp="2accb4zopyshw">
          <StatCard label="Guard Rejections (P-3)" value={data.guardData.totalFatalRejections} icon={<ShieldAlert className="w-4 h-4 text-rose-500" data-id="element-7135" data-mp="8k7cl5cpswy7" />} data-id="element-7134" data-mp="2addppe4y1x4d" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7136" data-mp="clj79pag48d9">
          <StatCard label="Totaal Vragen" value={data.totalAttempts} icon={<Activity className="w-4 h-4 text-blue-500" data-id="element-7138" data-mp="3jyyajadxqupm" />} data-id="element-7137" data-mp="ck82pn96ewl3" />
        </motion.div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-7139" data-mp="3f1pnavdxk0hi">
        <motion.div variants={itemVariants} data-id="element-7140" data-mp="351yt6uta7nhy">
          <Card className="p-5 h-full" data-id="element-7141" data-mp="35537wm4di1mv">
            <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-7142" data-mp="311na3mgkhv8l">
              <BarChart2 className="w-4 h-4 text-indigo-500" data-id="element-7143" data-mp="2wa56srr2nikg" />
              Tier Distributie
            </h3>
            <div className="space-y-2" data-id="element-7144" data-mp="2rasomkb5azo7">
              {Array.from({
              length: 16
            }, (_, i) => i + 1).map(tier => {
              const count = data.tierCounts[tier] || 0;
              const pct = count / maxTierCount * 100;
              if (count === 0 && tier > 8) return null; // Hide empty higher tiers to save space
              return <div key={tier} className="flex items-center gap-3" data-id="element-7145" data-mp="stihlm9yoyre">
                    <div className="w-12 text-xs font-medium text-slate-500 text-right" data-id="element-7146" data-mp="wu2c0i35ho9u">
                      Tier {tier}
                    </div>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-7147" data-mp="16tjg4v2r7u3p">
                      <div className="h-full bg-indigo-500 rounded-full" style={{
                    width: `${pct}%`
                  }} data-id="element-7148" data-mp="16r722t04a3vx" />
                    </div>
                    <div className="w-10 text-xs font-semibold text-slate-700" data-id="element-7149" data-mp="j2xilxqdls7y">
                      {count}
                    </div>
                  </div>;
            })}
            </div>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants} data-id="element-7150" data-mp="3miuwagnqxib1">
          <Card className="p-5 h-full" data-id="element-7151" data-mp="3rj0fr9jv3yi1">
            <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-7152" data-mp="3rhxnoyaszq0j">
              <ShieldAlert className="w-4 h-4 text-rose-500" data-id="element-7153" data-mp="3jh9mh1238u72" />
              Pedagogical Guard (Top Redenen)
            </h3>
            {data.guardData.topReasons.length > 0 ? <div className="space-y-4" data-id="element-7154" data-mp="3t7kr6ptlj7f0">
                {data.guardData.topReasons.map((reason, i) => <div key={i} data-id="element-7155" data-mp="3oacgfaas25ns" data-mpi={i}>
                    <div className="flex justify-between text-sm mb-1" data-id="element-7156" data-mp="3oc5h88yplvnv">
                      <span className="font-medium text-slate-700" data-id="element-7157" data-mp="30ki732po2z8a">
                        {reason.reason}
                      </span>
                      <span className="font-bold text-slate-900" data-id="element-7158" data-mp="3ai4nxuqj10uw">
                        {reason.count}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-7159" data-mp="35i05hz625w21">
                      <div className="h-full bg-rose-500 rounded-full" style={{
                  width: `${data.guardData.totalFatalRejections > 0 ? reason.count / data.guardData.totalFatalRejections * 100 : 0}%`
                }} data-id="element-7160" data-mp="pqvwc13dlm56" />
                    </div>
                  </div>)}
                <div className="pt-4 mt-4 border-t border-slate-100" data-id="element-7161" data-mp="1tr2fgkce9y7j">
                  <div className="flex justify-between text-sm" data-id="element-7162" data-mp="1yoluvnktwh12">
                    <span className="text-slate-500" data-id="element-7163" data-mp="1yr855uz3tamz">
                      Vervangingsratio (Success)
                    </span>
                    <span className="font-bold text-emerald-600" data-id="element-7164" data-mp="2qg1ow2opt61b">
                      {Math.round(data.guardData.replacementSuccessRate * 100)}%
                    </span>
                  </div>
                </div>
              </div> : <div className="text-sm text-slate-500 text-center py-8" data-id="element-7165" data-mp="2hm59d6q22ruv">
                Geen guard rejections geregistreerd.
              </div>}
          </Card>
        </motion.div>
      </motion.div>
    </PageLayout>;
}